import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import type { Vec2 } from '../../../geo/types';
import { PATTERN } from '../../../render/patterns/patternIds';
import { SURFACE } from '../../mesh/attributes';
import type { MeshBuilder } from '../../mesh/meshBuilder';
import { gableRoof } from '../roofSolids';
import { horizontalCap, pyramidOverRing, ringPoints } from '../solids';
import { corners } from './genericBlock';
import { buildVessel } from './churchVessel';
import { buildCloisterArcade } from './cloisterArcade';
import { buildConventEnclosure } from './conventEnclosure';
import { buildConventRange } from './conventRange';
import { buildConventSoil } from './conventSoil';
import { centredWindow, rectWall } from './masonry';
import type { LandmarkContext } from './landmarkFeature';
import {
  KARTUIZERS,
  kartuizersChapel,
  kartuizersFloors,
  kartuizersFrame,
  kartuizersPartFrame,
} from './kartuizersPlan';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { meshFrameOf, toLayout } from './planFrame';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';

/** A great cloister with separate cells, not four communal dormitory ranges. */
export function buildKartuizers(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors & {
    readonly tile: LinearRGB;
    readonly ground: LinearRGB;
    readonly garden: LinearRGB;
  },
  ids: { readonly edgeId: number; readonly seed: number },
  tracery: TracerySink = NO_TRACERY,
  context: Pick<LandmarkContext, 'elevation' | 'elevationAt'> = {}
): void {
  const plan = kartuizersFrame(landmark),
    frame = meshFrameOf(plan),
    styles = paintedChapelStyles(colors, ids, tracery),
    brick = { ...styles, wall: { ...styles.wall, surface: SURFACE.brick } },
    tile = {
      ...brick,
      roof: {
        ...brick.roof,
        color: colors.tile,
        surface: SURFACE.roofTile,
        pattern: PATTERN.roofTileHollePan,
      },
    },
    chapel = kartuizersChapel(),
    cf = meshFrameOf(kartuizersPartFrame(plan, KARTUIZERS.chapel));
  buildVessel(builder, brick, cf, chapel, {
    longWindows: { sill: 3, top: 10.1 },
    westWindow: true,
    bay: 5.5,
  });
  const bell = KARTUIZERS.belfry,
    ring = ringPoints([bell.u, bell.v], bell.radius, 8, Math.PI / 8);
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length]!,
      width = Math.hypot(b[0] - a[0], b[1] - a[1]);
    rectWall(
      builder,
      brick,
      cf,
      [a, b],
      [bell.foot, bell.eaves],
      centredWindow(width, 0.3, bell.eaves - bell.foot - 0.35, 0.5),
      bell.foot
    );
  });
  horizontalCap(builder, brick.wall, cf, ring, bell.foot, false);
  pyramidOverRing(builder, brick, cf, ring, bell.eaves, { plan: [bell.u, bell.v], y: bell.tip });
  for (const part of [...KARTUIZERS.cells, ...KARTUIZERS.ranges])
    buildConventRange(
      builder,
      meshFrameOf(kartuizersPartFrame(plan, part)),
      part.id === 'library-mary-chapel' ? brick : tile,
      part
    );
  for (const w of KARTUIZERS.walks) {
    buildCloisterArcade(builder, brick.wall, frame, {
      ...w,
      front: w.front.map((p) => [p[0]!, p[1]!] as Vec2) as [Vec2, Vec2],
    });
    const r = w.rect,
      roofFrame = w.ridgeAlongU
        ? frame
        : meshFrameOf(
            kartuizersPartFrame(plan, {
              u: (r.u0 + r.u1) / 2,
              v: (r.v0 + r.v1) / 2,
              angleDeg: 90,
            })
          ),
      rect = w.ridgeAlongU
        ? r
        : {
            u0: -(r.v1 - r.v0) / 2,
            u1: (r.v1 - r.v0) / 2,
            v0: -(r.u1 - r.u0) / 2,
            v1: (r.u1 - r.u0) / 2,
          };
    gableRoof(builder, tile, roofFrame, {
      ...rect,
      eaveY: 3.6,
      ridgeY: 4.8,
      ends: { start: true, end: true },
    });
    horizontalCap(
      builder,
      { ...brick.wall, surface: SURFACE.wood },
      roofFrame,
      corners(rect),
      3.56,
      false
    );
  }
  const base = context.elevation ?? 0,
    terrain = context.elevationAt ?? (() => base);
  buildConventEnclosure(
    builder,
    brick.wall,
    frame,
    (p) => terrain(toLayout(plan, p)) - base,
    KARTUIZERS.walls
  );
  for (const floor of kartuizersFloors(landmark))
    buildConventSoil(
      builder,
      { ...brick.wall, color: floor.garden ? colors.garden : colors.ground },
      floor.ring,
      terrain,
      base,
      floor.height,
      floor.garden
    );
}
